/**
 * @fileoverview Editable Horizontal Rule TipTap extension
 *
 * Purpose: Replaces TipTap's default `HorizontalRule` node with one that stores
 * `color`, `thickness` (px) and `width` (% or px or "auto") as node attributes
 * and serialises them as inline `style` so they round-trip cleanly through
 * `editor.getHTML()` → DOMPurify → `.prose-letter` preview → re-load into
 * TipTap → Word/PDF export.
 *
 * Used by:
 * - `template-editor.tsx` (replaces StarterKit's HR via `horizontalRule: false`)
 * - HR toolbar segment in the same file (commands `setHrColor`, `setHrThickness`,
 *   `setHrWidth`)
 *
 * Inline-style shape produced:
 *   <hr style="border:none;height:2px;background:#1B3A5C;width:100%;margin:8px auto" />
 *
 * Both this extension and the AI extraction prompt
 * (`convex/settings/precedents/actions.ts`) emit the same shape so saved
 * templates and freshly-extracted templates render identically.
 */

import HorizontalRule from "@tiptap/extension-horizontal-rule";

export const DEFAULT_HR_COLOR = "#1B3A5C";
export const DEFAULT_HR_THICKNESS = 2;
export const DEFAULT_HR_WIDTH = "100%";
export const DEFAULT_HR_ALIGNMENT: HrAlignment = "left";

export type HrAlignment = "left" | "center" | "right";

export interface HrAttrs {
  color: string;
  thickness: number;
  width: string;
  alignment: HrAlignment;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    editableHorizontalRule: {
      setHrColor: (color: string) => ReturnType;
      setHrThickness: (thickness: number) => ReturnType;
      setHrWidth: (width: string) => ReturnType;
      setHrAlignment: (alignment: HrAlignment) => ReturnType;
    };
  }
}

/**
 * Map an alignment intent to the horizontal margin tokens we serialise.
 * - left: `margin: 8px 0`
 * - center: `margin: 8px auto`
 * - right: `margin: 8px 0 8px auto`
 */
function alignmentToMargin(alignment: HrAlignment): string {
  switch (alignment) {
    case "center":
      return "8px auto";
    case "right":
      return "8px 0 8px auto";
    case "left":
    default:
      return "8px 0";
  }
}

/**
 * Reverse-map a `margin` value to an HrAlignment.
 * Used when parsing HRs the AI extracted (which write margin directly) so the
 * editor toolbar shows the right alignment as the active state.
 */
function marginToAlignment(margin: string): HrAlignment {
  const tokens = margin
    .trim()
    .split(/\s+/)
    .map((t) => t.toLowerCase());
  // shorthand: <top> <horizontal> [<bottom> <horizontal>]
  // 2-token form: <vertical> <horizontal>
  // 4-token form: <top> <right> <bottom> <left>
  if (tokens.length === 2) {
    if (tokens[1] === "auto") return "center";
    return "left";
  }
  if (tokens.length === 4) {
    const [, right, , left] = tokens;
    if (right === "auto" && left === "auto") return "center";
    if (right === "0" && left === "auto") return "right";
    return "left";
  }
  return DEFAULT_HR_ALIGNMENT;
}

/**
 * Return the LAST capture group match for `pattern` in `text`, or `null` if
 * none found. Mirrors the CSS cascade (later declarations win) so an HR with
 * a duplicated `style` attribute — produced by a now-fixed PreserveStyles
 * round-trip bug — parses to the value the user actually saw on screen.
 */
function lastMatchGroup(text: string, pattern: RegExp): string | null {
  const flags = pattern.flags.includes("g")
    ? pattern.flags
    : `${pattern.flags}g`;
  const re = new RegExp(pattern.source, flags);
  const matches = [...text.matchAll(re)].filter((m) => m[1] != null);
  return matches.length > 0 ? matches[matches.length - 1][1] : null;
}

/**
 * Pull `<hr>` attributes out of an inline `style` string.
 * Returns sensible defaults for any property the source HR doesn't specify, so
 * legacy HRs (no inline style) become editable from a known starting point.
 */
function parseHrStyle(style: string | null): HrAttrs {
  const result: HrAttrs = {
    color: DEFAULT_HR_COLOR,
    thickness: DEFAULT_HR_THICKNESS,
    width: DEFAULT_HR_WIDTH,
    alignment: DEFAULT_HR_ALIGNMENT,
  };
  if (!style) return result;

  const color = lastMatchGroup(
    style,
    /(?:background(?:-color)?|color)\s*:\s*([^;]+)/i,
  );
  if (color) result.color = color.trim();

  const height = lastMatchGroup(style, /height\s*:\s*([\d.]+)\s*px/i);
  if (height) result.thickness = parseFloat(height);

  const width = lastMatchGroup(style, /(?:^|;)\s*width\s*:\s*([^;]+)/i);
  if (width) result.width = width.trim();

  const margin = lastMatchGroup(style, /(?:^|;)\s*margin\s*:\s*([^;]+)/i);
  if (margin) result.alignment = marginToAlignment(margin);

  return result;
}

/**
 * Build the inline `style` value the renderer + serialiser both use.
 * Keeping a single source of truth avoids drift between extraction prompt,
 * editor, and read-only preview.
 */
function buildHrStyle({ color, thickness, width, alignment }: HrAttrs): string {
  return `border:none;height:${thickness}px;background:${color};width:${width};margin:${alignmentToMargin(alignment)}`;
}

export const EditableHorizontalRule = HorizontalRule.extend({
  name: "horizontalRule",

  addAttributes() {
    return {
      color: {
        default: DEFAULT_HR_COLOR,
        parseHTML: (element) =>
          parseHrStyle(element.getAttribute("style")).color,
        renderHTML: () => ({}),
      },
      thickness: {
        default: DEFAULT_HR_THICKNESS,
        parseHTML: (element) =>
          parseHrStyle(element.getAttribute("style")).thickness,
        renderHTML: () => ({}),
      },
      width: {
        default: DEFAULT_HR_WIDTH,
        parseHTML: (element) =>
          parseHrStyle(element.getAttribute("style")).width,
        renderHTML: () => ({}),
      },
      alignment: {
        default: DEFAULT_HR_ALIGNMENT,
        parseHTML: (element) =>
          parseHrStyle(element.getAttribute("style")).alignment,
        renderHTML: () => ({}),
      },
    };
  },

  renderHTML({ HTMLAttributes, node }) {
    const attrs: HrAttrs = {
      color: (node.attrs.color as string) ?? DEFAULT_HR_COLOR,
      thickness: (node.attrs.thickness as number) ?? DEFAULT_HR_THICKNESS,
      width: (node.attrs.width as string) ?? DEFAULT_HR_WIDTH,
      alignment: (node.attrs.alignment as HrAlignment) ?? DEFAULT_HR_ALIGNMENT,
    };
    return [
      "hr",
      {
        ...HTMLAttributes,
        style: buildHrStyle(attrs),
      },
    ];
  },

  addCommands() {
    return {
      // Preserve parent's `setHorizontalRule` (and any future commands)
      // Without this, the toolbar's "Insert HR" button throws
      // "editor.chain(...).focus(...).setHorizontalRule is not a function".
      ...this.parent?.(),
      setHrColor:
        (color: string) =>
        ({ commands }) =>
          commands.updateAttributes(this.name, { color }),
      setHrThickness:
        (thickness: number) =>
        ({ commands }) =>
          commands.updateAttributes(this.name, { thickness }),
      setHrWidth:
        (width: string) =>
        ({ commands }) =>
          commands.updateAttributes(this.name, { width }),
      setHrAlignment:
        (alignment: HrAlignment) =>
        ({ commands }) =>
          commands.updateAttributes(this.name, { alignment }),
    };
  },
});
